import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { neoApi, type NeoPlanStep, type NeoRoutine, type ApiError } from '../api';
import { Page, Card, Button, Markdown, EmptyState, Field, Select, Textarea } from '../ui';

/**
 * Plano do Projeto (genérico) — o Planejador lê os documentos de fundação e propõe
 * as ETAPAS pra tirar o projeto do papel. O humano revisa/edita/aprova e executa
 * cada etapa: 'content' e 'agent' rodam no Hermes; 'code' aguarda o motor Claude.
 */

const KIND_UI: Record<NeoPlanStep['kind'], { label: string; icon: string; hint: string }> = {
  content: { label: 'Documento', icon: '📄', hint: 'um material escrito pra você pela IA' },
  agent: { label: 'Assistente', icon: '🤖', hint: 'um assistente de IA que trabalha no seu projeto' },
  code: { label: 'Site & Sistema', icon: '🌐', hint: 'algo publicado no seu site — nós cuidamos' },
};

const STATUS_UI: Record<NeoPlanStep['status'], { label: string; color: string; bg: string }> = {
  proposed: { label: 'Proposto', color: '#64748b', bg: 'rgba(148,163,184,0.14)' },
  approved: { label: 'Aprovado', color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  running: { label: 'Executando…', color: '#d97706', bg: 'rgba(251,191,36,0.15)' },
  done: { label: 'Pronto', color: '#059669', bg: 'rgba(52,211,153,0.15)' },
  failed: { label: 'Falhou', color: '#dc2626', bg: 'rgba(251,113,133,0.15)' },
  skipped: { label: 'Ignorado', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
};

export function NeoPlan() {
  const { slug } = useParams<{ slug: string }>();
  const [steps, setSteps] = useState<NeoPlanStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [routine, setRoutineState] = useState<NeoRoutine | null>(null);
  const [rBusy, setRBusy] = useState(false);
  const [rMsg, setRMsg] = useState<string | null>(null);
  // Adicionar etapa manual (além das geradas pela IA)
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addType, setAddType] = useState<'publish' | 'content' | 'site' | 'agent'>('publish');
  const [addSub, setAddSub] = useState(''); // subdomínio (só p/ tipo publicar)
  const [addBusy, setAddBusy] = useState(false);

  async function load() {
    if (!slug) return;
    try {
      const r = await neoApi.plan(slug);
      setSteps(r.steps);
      const rt = await neoApi.routine(slug);
      setRoutineState(rt.routine);
    } catch {
      /* silencioso — mostra vazio */
    } finally {
      setLoading(false);
    }
  }

  async function toggleRoutine(active: boolean) {
    if (!slug) return;
    setRBusy(true);
    setRMsg(null);
    try {
      const r = await neoApi.setRoutine(slug, { active });
      setRoutineState(r.routine);
    } catch {
      setRMsg('Não consegui atualizar a rotina.');
    } finally {
      setRBusy(false);
    }
  }

  async function runRoutineNow() {
    if (!slug) return;
    setRBusy(true);
    setRMsg(null);
    try {
      await neoApi.runRoutineNow(slug);
      setRMsg('Publicando um artigo agora… aparece no site em alguns minutos.');
    } catch {
      setRMsg('Não consegui publicar agora.');
    } finally {
      setRBusy(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Enquanto houver etapa em execução (ex: site sendo construído), atualiza sozinho.
  useEffect(() => {
    if (!steps.some((s) => s.status === 'running')) return;
    const t = setInterval(() => void load(), 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, slug]);

  async function generate() {
    if (!slug) return;
    setError(null);
    setGenerating(true);
    try {
      const r = await neoApi.generatePlan(slug);
      setSteps(r.steps);
    } catch (e) {
      setError((e as ApiError)?.error === 'planner_failed' ? 'O planejador não respondeu agora. Tente de novo.' : 'Não consegui gerar o plano.');
    } finally {
      setGenerating(false);
    }
  }

  async function patchStep(id: string, patch: Parameters<typeof neoApi.updateStep>[2]) {
    if (!slug) return;
    const r = await neoApi.updateStep(slug, id, patch);
    setSteps((s) => s.map((x) => (x.id === id ? r.step : x)));
  }

  async function run(id: string) {
    if (!slug) return;
    setBusyId(id);
    setError(null);
    try {
      const r = await neoApi.runStep(slug, id);
      setSteps((s) => s.map((x) => (x.id === id ? r.step : x)));
    } catch {
      setError('Não consegui executar a etapa.');
    } finally {
      setBusyId(null);
    }
  }

  async function del(id: string) {
    if (!slug || !confirm('Remover esta etapa do plano?')) return;
    await neoApi.deleteStep(slug, id);
    setSteps((s) => s.filter((x) => x.id !== id));
  }

  function startEdit(step: NeoPlanStep) {
    setEditId(step.id);
    setEditTitle(step.title);
    setEditDesc(step.description);
  }
  async function saveEdit() {
    if (!editId) return;
    await patchStep(editId, { title: editTitle.trim() || 'Etapa', description: editDesc });
    setEditId(null);
  }

  async function addStep() {
    if (!slug || !addTitle.trim() || addBusy) return;
    setAddBusy(true);
    setError(null);
    const cfg =
      addType === 'content'
        ? { kind: 'content' as const }
        : addType === 'agent'
          ? { kind: 'agent' as const }
          : addType === 'publish'
            ? { kind: 'code' as const, spec: { action: 'publish', ...(addSub.trim() ? { subdomain: addSub.trim() } : {}) } as Record<string, unknown> }
            : { kind: 'code' as const };
    try {
      const r = await neoApi.createStep(slug, {
        title: addTitle.trim(),
        ...(addDesc.trim() ? { description: addDesc.trim() } : {}),
        kind: cfg.kind,
        ...('spec' in cfg ? { spec: cfg.spec } : {}),
      });
      setSteps((s) => [...s, r.step]);
      setAddTitle('');
      setAddDesc('');
      setShowAdd(false);
    } catch {
      setError('Não consegui adicionar a etapa.');
    } finally {
      setAddBusy(false);
    }
  }

  const hasPlan = steps.length > 0;

  return (
    <Page
      title="Plano do Projeto"
      subtitle="A IA lê seu planejamento e propõe as etapas pra executar. Você revisa, aprova e roda cada passo."
      actions={
        hasPlan ? (
          <>
            <Button variant="ghost" onClick={() => setShowAdd((v) => !v)}>➕ Adicionar etapa</Button>
            <Button variant="ghost" disabled={generating} onClick={generate}>
              {generating ? 'Gerando…' : 'Regenerar propostas'}
            </Button>
          </>
        ) : undefined
      }
    >
      {error && <div className="ui-error-banner" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}

      {routine && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Card size="normal">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 22 }}>{routine.active ? '🟢' : '⚪'}</span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 600 }}>Publicação automática {routine.active ? 'ligada' : 'desligada'}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>
                  {routine.active ? `O blog publica 1 artigo por dia sozinho.` : 'O blog não está publicando sozinho.'}
                  {routine.lastRunAt ? ` Última publicação: ${new Date(routine.lastRunAt).toLocaleString('pt-BR')}.` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button variant="ghost" disabled={rBusy} onClick={() => toggleRoutine(!routine.active)}>
                  {routine.active ? 'Desligar' : 'Ligar'}
                </Button>
                <Button variant="primary" disabled={rBusy} onClick={runRoutineNow}>
                  Publicar agora
                </Button>
              </div>
            </div>
            {rMsg && <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>{rMsg}</div>}
          </Card>
        </div>
      )}

      {showAdd && (
        <Card size="normal" style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>Nova etapa</div>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <Field label="Tipo">
              <Select value={addType} onChange={(e) => setAddType(e.target.value as typeof addType)}>
                <option value="publish">🚀 Publicar no subdomínio</option>
                <option value="content">📄 Documento</option>
                <option value="site">🌐 Construir site</option>
                <option value="agent">🤖 Assistente</option>
              </Select>
            </Field>
            <Field label="Título">
              <input
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                placeholder={addType === 'publish' ? 'Publicar o site no subdomínio' : 'O que essa etapa faz'}
              />
            </Field>
            <Field label="Descrição (opcional)">
              <Textarea
                value={addDesc}
                onChange={(e) => setAddDesc(e.target.value)}
                rows={2}
                placeholder={addType === 'publish' ? 'Coloca o site no ar no subdomínio do projeto' : ''}
              />
            </Field>
            {addType === 'publish' && (
              <>
                <Field label="Subdomínio (só o nome — ex.: concurso)">
                  <input value={addSub} onChange={(e) => setAddSub(e.target.value.replace(/[^a-z0-9-]/gi, '').toLowerCase())} placeholder="concurso" />
                </Field>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>
                  Publica o site já construído em <strong>{(addSub.trim() || 'novo-projeto')}.zighue.com.br</strong> (DNS + nginx + certificado). Não reconstrói o site. Vazio = usa o nome do projeto.
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button variant="primary" disabled={addBusy || !addTitle.trim()} onClick={addStep}>
                {addBusy ? 'Adicionando…' : 'Adicionar etapa'}
              </Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancelar</Button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div style={{ color: 'var(--color-fg-muted)' }}>Carregando…</div>
      ) : !hasPlan ? (
        <EmptyState
          icon={<span style={{ fontSize: 32 }}>🗺️</span>}
          title="Ainda não há um plano"
          description="A partir do diagnóstico, posicionamento, growth e identidade, a IA monta o passo a passo do seu projeto — do que produzir aos agentes e ao que é código."
          action={
            <Button variant="primary" disabled={generating} onClick={generate}>
              {generating ? 'Gerando plano…' : 'Gerar plano'}
            </Button>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {steps.map((step, i) => {
            const k = KIND_UI[step.kind];
            const st = STATUS_UI[step.status];
            const editing = editId === step.id;
            const result = (step.result ?? {}) as Record<string, unknown>;
            const files = Array.isArray(result['files']) ? (result['files'] as string[]) : [];
            const siteUrl = typeof result['url'] === 'string' ? (result['url'] as string) : null;
            return (
              <Card key={step.id} size="normal">
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                      background: 'var(--color-bg-subtle)', color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)', fontWeight: 600,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* linha de badges */}
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
                      <span title={k.hint} style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px', borderRadius: 999, background: 'var(--color-bg-subtle)', color: 'var(--color-fg-muted)' }}>
                        {k.icon} {k.label}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color, fontWeight: 600 }}>
                        {st.label}
                      </span>
                    </div>

                    {editing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        <input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          style={{ padding: 'var(--space-2)', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-fg)', font: 'inherit', fontWeight: 600 }}
                        />
                        <textarea
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          rows={3}
                          style={{ padding: 'var(--space-2)', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-fg)', font: 'inherit', resize: 'vertical' }}
                        />
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <Button variant="primary" onClick={saveEdit}>Salvar</Button>
                          <Button variant="ghost" onClick={() => setEditId(null)}>Cancelar</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{step.title}</div>
                        {step.description && <div style={{ color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>{step.description}</div>}
                      </>
                    )}

                    {/* resultado */}
                    {step.status === 'done' && step.kind === 'content' && typeof result['preview'] === 'string' && (
                      <details style={{ marginTop: 'var(--space-3)' }}>
                        <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-accent)' }}>
                          Ver documento gerado {step.outputPath ? `(${step.outputPath})` : ''}
                        </summary>
                        <div style={{ marginTop: 'var(--space-2)', padding: 'var(--space-3)', background: 'var(--color-bg-subtle)', borderRadius: 8, maxHeight: 320, overflow: 'auto' }}>
                          <Markdown text={String(result['preview'])} />
                        </div>
                      </details>
                    )}
                    {step.status === 'done' && step.kind === 'agent' && (
                      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        ✅ Agente <strong>{String(result['name'] ?? '')}</strong> criado.{' '}
                        <Link to={`/p/${slug}/agents`} style={{ color: 'var(--color-accent)' }}>Ver agentes</Link>
                      </div>
                    )}
                    {step.kind === 'code' && step.status === 'running' && (
                      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: '#b45309', background: 'rgba(251,191,36,0.12)', padding: 'var(--space-2) var(--space-3)', borderRadius: 8 }}>
                        🛠️ A IA está construindo seu site agora… leva alguns minutos. Pode deixar esta página aberta — ela atualiza sozinha.
                      </div>
                    )}
                    {step.kind === 'code' && step.status === 'done' && (
                      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        {siteUrl ? (
                          <span>
                            🌐 <strong>No ar:</strong>{' '}
                            <a href={siteUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)' }}>
                              {siteUrl.replace(/^https?:\/\//, '')} ↗
                            </a>
                          </span>
                        ) : (
                          <span>🌐 <strong>Site construído!</strong> {files.length > 0 ? `(${files.length} arquivos)` : ''} A publicação no seu endereço é o próximo passo.</span>
                        )}
                        {files.length > 0 && (
                          <details style={{ marginTop: 'var(--space-1)' }}>
                            <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>Ver arquivos criados</summary>
                            <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>
                              {files.slice(0, 40).map((f) => <li key={f}>{f}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                    {step.status === 'failed' && step.error && (
                      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-danger, #dc2626)' }}>
                        Não deu certo desta vez. Tente executar de novo.
                      </div>
                    )}

                    {/* Detalhes técnicos — escondidos por padrão (só pra quem quiser ver) */}
                    {step.spec && Object.keys(step.spec).length > 0 && (
                      <details style={{ marginTop: 'var(--space-2)' }}>
                        <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>
                          Detalhes técnicos
                        </summary>
                        <pre style={{ marginTop: 'var(--space-1)', padding: 'var(--space-2)', background: 'var(--color-bg-subtle)', borderRadius: 6, fontSize: 'var(--font-size-xs)', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                          {JSON.stringify(step.spec, null, 2)}
                        </pre>
                      </details>
                    )}

                    {/* ações */}
                    {!editing && (
                      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
                        {step.status === 'proposed' && (
                          <Button variant="primary" onClick={() => patchStep(step.id, { status: 'approved' })}>Aprovar</Button>
                        )}
                        {(step.status === 'approved' || step.status === 'failed') && (
                          <Button variant="primary" disabled={busyId === step.id} onClick={() => run(step.id)}>
                            {busyId === step.id
                              ? 'Iniciando…'
                              : step.status === 'failed'
                                ? 'Tentar de novo'
                                : step.kind === 'code'
                                  ? 'Construir agora'
                                  : 'Executar'}
                          </Button>
                        )}
                        <Button variant="ghost" onClick={() => startEdit(step)}>Editar</Button>
                        <Button variant="ghost" onClick={() => del(step.id)}>Excluir</Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Page>
  );
}
